import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  Plus, 
  Search, 
  Filter, 
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  TrendingUp,
  User,
  CreditCard,
  CalendarDays,
  Receipt,
  Wallet,
  Users,
  BookOpen
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const AccountsReceivablePage = () => {
  const { company, person } = useAuth();
  const navigate = useNavigate();
  const [receivables, setReceivables] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showNewReceivableModal, setShowNewReceivableModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('receivables'); // 'receivables' ou 'credits'

  const [newReceivable, setNewReceivable] = useState({
    person_id: '',
    description: '',
    amount: '',
    due_date: '',
    type: 'service',
    notes: '',
    priority: 'medium'
  });

  const [newCredit, setNewCredit] = useState({
    person_id: '',
    amount: '',
    description: 'Crédito manual',
    notes: ''
  });  useEffect(() => {
    if (company?.id) {
      loadData();
    }
  }, [company]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar pessoas (clientes, alunos, responsáveis)
      const peopleData = await dataHelpers.getPeople(company.id);
      const clientsPeople = peopleData?.filter(person => 
        ['customer', 'student', 'parent'].includes(person.type)
      ) || [];
      setPeople(clientsPeople);
      
      const receivablesData = await dataHelpers.getAccountsReceivable(company.id);
      setReceivables(receivablesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'overdue':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Pago';
      case 'pending':
        return 'Pendente';
      case 'overdue':
        return 'Vencido';
      default:
        return status;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'fee':
        return 'bg-blue-100 text-blue-800';
      case 'product':
        return 'bg-purple-100 text-purple-800';
      case 'service':
        return 'bg-orange-100 text-orange-800';
      case 'credit':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'fee':
        return 'Taxa';
      case 'product':
        return 'Produto';
      case 'service':
        return 'Serviço';
      case 'credit':
        return 'Crédito';
      default:
        return type;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500';
      case 'medium':
        return 'border-l-yellow-500';
      case 'low':
        return 'border-l-green-500';
      default:
        return 'border-l-gray-500';
    }
  };

  const filteredReceivables = receivables.filter(receivable => {
    const studentName = receivable.students?.name || receivable.payer_name || '';
    const parentName = receivable.payer_name || '';
    const description = receivable.description || '';
    
    const matchesSearch = studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         parentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || receivable.status === statusFilter;
    const matchesType = typeFilter === 'all' || receivable.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleCreateReceivable = async () => {
    try {
      const selectedPersonData = people.find(p => p.id === parseInt(newReceivable.person_id));
      if (!selectedPersonData) {
        toast.error('Selecione uma pessoa válida');
        return;
      }

      const receivableData = {
        person_id: parseInt(newReceivable.person_id),
        payer_name: selectedPersonData.name,
        payer_email: selectedPersonData.email,
        payer_phone: selectedPersonData.phone,
        description: newReceivable.description,
        amount: parseFloat(newReceivable.amount),
        due_date: newReceivable.due_date,
        type: newReceivable.type,
        notes: newReceivable.notes,
        priority: newReceivable.priority,
        created_by: person.id,
        status: 'pending'
      };
      
      await dataHelpers.createAccountsReceivable(company.id, receivableData);
      toast.success('Conta a receber criada com sucesso!');
      
      setShowNewReceivableModal(false);
      setNewReceivable({
        person_id: '',
        description: '',
        amount: '',
        due_date: '',
        type: 'service',
        notes: '',
        priority: 'medium'
      });
      
      loadData();
    } catch (error) {
      console.error('Error creating receivable:', error);
      toast.error('Erro ao criar conta a receber');
    }
  };  const handleAddCredit = async () => {
    console.log('� DEBUG: Iniciando handleAddCredit');
    
    try {
      // Validações básicas
      if (!newCredit.person_id || !newCredit.amount) {
        console.log('🔥 DEBUG: Validação falhou - dados incompletos');
        toast.error('Selecione uma pessoa e informe o valor');
        return;
      }

      const creditAmount = parseFloat(newCredit.amount);
      if (isNaN(creditAmount) || creditAmount <= 0) {
        console.log('🔥 DEBUG: Validação falhou - valor inválido');
        toast.error('Informe um valor válido maior que zero');
        return;
      }

      const selectedPerson = people.find(p => p.id === parseInt(newCredit.person_id));
      if (!selectedPerson) {
        console.log('🔥 DEBUG: Validação falhou - pessoa não encontrada');
        toast.error('Pessoa não encontrada');
        return;
      }

      console.log('� DEBUG: Validações OK');
      console.log('🔥 DEBUG: selectedPerson:', selectedPerson);
      console.log('� DEBUG: creditAmount:', creditAmount);
      console.log('🔥 DEBUG: company:', company);
      console.log('🔥 DEBUG: person:', person);

      // Verificar se a função existe
      if (!dataHelpers?.addPersonCredit) {
        console.error('🔥 DEBUG: dataHelpers.addPersonCredit não existe');
        toast.error('Erro interno: Função não encontrada');
        return;
      }

      console.log('� DEBUG: Preparando dados para envio...');
      
      const creditData = {
        person_id: parseInt(newCredit.person_id),
        amount: creditAmount,
        description: newCredit.description || 'Crédito manual',
        notes: newCredit.notes || ''
      };

      console.log('🔥 DEBUG: creditData final:', creditData);
      console.log('� DEBUG: Chamando dataHelpers.addPersonCredit...');
      
      // Chamar a função
      const result = await dataHelpers.addPersonCredit(company.id, creditData, person.id);

      console.log('� DEBUG: Resultado recebido:', result);

      if (result?.success) {
        console.log('🔥 DEBUG: Sucesso! Mostrando toast...');
        toast.success(`Crédito de ${formatCurrency(creditAmount)} adicionado ao saldo de ${selectedPerson.name}`);
        
        // Fechar modal e limpar campos
        setShowCreditModal(false);
        setNewCredit({
          person_id: '',
          amount: '',
          description: 'Crédito manual',
          notes: ''
        });
        
        console.log('🔥 DEBUG: Recarregando dados...');
        await loadData();
        console.log('🔥 DEBUG: Operação concluída com sucesso!');
      } else {
        throw new Error('Resultado da operação indica falha');
      }
      
    } catch (error) {
      console.error('🚨 ERROR: Erro em handleAddCredit:', error);
      console.error('🚨 ERROR: Stack trace:', error.stack);
      
      let errorMessage = 'Erro ao adicionar crédito';
      if (error.message?.includes('não encontrada')) {
        errorMessage = 'Pessoa não encontrada no sistema';
      } else if (error.message?.includes('Company ID')) {
        errorMessage = 'Erro de configuração da empresa';
      } else if (error.message?.includes('Person ID')) {
        errorMessage = 'Erro na identificação da pessoa';
      } else if (error.message?.includes('Valor do crédito')) {
        errorMessage = 'Valor do crédito inválido';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const calculateTotals = () => {
    const total = filteredReceivables.reduce((sum, receivable) => sum + receivable.amount, 0);
    const paid = filteredReceivables.filter(r => r.status === 'paid').reduce((sum, receivable) => sum + receivable.amount, 0);
    const pending = filteredReceivables.filter(r => r.status === 'pending').reduce((sum, receivable) => sum + receivable.amount, 0);
    const overdue = filteredReceivables.filter(r => r.status === 'overdue').reduce((sum, receivable) => sum + receivable.amount, 0);
    
    return { total, paid, pending, overdue };
  };

  const totals = calculateTotals();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contas a Receber</h1>
            <p className="text-gray-600">
              Gerencie cobranças e créditos das pessoas
            </p>
          </div>
          <div className="mt-4 sm:mt-0 space-x-2">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>            <Button 
              variant="outline"
              onClick={() => setShowCreditModal(true)}
              className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Adicionar Crédito
            </Button>
            <Button onClick={() => setShowNewReceivableModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Cobrança
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 bg-gradient-to-r from-gray-100 to-gray-200 p-1.5 rounded-xl shadow-inner w-fit">
          <button
            onClick={() => setActiveTab('receivables')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'receivables'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                : 'text-gray-700 hover:text-blue-600 hover:bg-white/50'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            <span>Contas a Receber</span>
          </button>
          <button
            onClick={() => setActiveTab('credits')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'credits'
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105'
                : 'text-gray-700 hover:text-green-600 hover:bg-white/50'
            }`}
          >
            <Wallet className="w-4 h-4" />
            <span>Lançamento de Créditos</span>
          </button>
        </div>

        {activeTab === 'receivables' ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totals.total)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Recebido</p>
                    <p className="text-2xl font-semibold text-green-600">
                      {formatCurrency(totals.paid)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-yellow-100">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pendente</p>
                    <p className="text-2xl font-semibold text-yellow-600">
                      {formatCurrency(totals.pending)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-red-100">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Vencido</p>
                    <p className="text-2xl font-semibold text-red-600">
                      {formatCurrency(totals.overdue)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Filters */}
            <Card className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar pessoa ou descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Todos os status</option>
                  <option value="pending">Pendente</option>
                  <option value="paid">Pago</option>
                  <option value="overdue">Vencido</option>
                </select>

                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Todos os tipos</option>
                  <option value="fee">Taxa</option>
                  <option value="product">Produto</option>
                  <option value="service">Serviço</option>
                </select>

                <Button variant="outline" className="justify-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Mais Filtros
                </Button>
              </div>
            </Card>

            {/* Receivables List */}
            <Card>
              <div className="overflow-hidden">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Pessoa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Descrição
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Vencimento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredReceivables.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">Nenhuma conta a receber encontrada</p>
                        </td>
                      </tr>
                    ) : (
                      filteredReceivables.map((receivable) => (
                        <tr key={receivable.id} className={`hover:bg-gray-50 border-l-4 ${getPriorityColor(receivable.priority)}`}>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {receivable.students?.name || 'Aluno não identificado'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {receivable.students?.class_name} • Resp: {receivable.payer_name}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900">{receivable.description}</p>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(receivable.type)}`}>
                              {getTypeLabel(receivable.type)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(receivable.amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {formatDate(receivable.due_date)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(receivable.status)}`}>
                              {getStatusIcon(receivable.status)}
                              <span className="ml-1">{getStatusLabel(receivable.status)}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right space-x-2">
                            <button
                              onClick={() => {
                                setSelectedReceivable(receivable);
                                setShowDetailsModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="text-gray-600 hover:text-gray-800">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="text-red-600 hover:text-red-800">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        ) : (
          /* Credits Tab */
          <div className="space-y-6">
            <Card className="p-6">
              <div className="text-center">
                <Wallet className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Lançamento de Créditos</h3>
                <p className="text-gray-600 mb-6">
                  Adicione créditos ao saldo das pessoas e mantenha um histórico detalhado das movimentações
                </p>
                <Button 
                  onClick={() => setShowCreditModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Crédito
                </Button>
              </div>
            </Card>

            {/* People Balance Overview */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Saldos das Pessoas</h3>
              </div>
              <div className="overflow-hidden">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Documento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Saldo Atual
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {people.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">Nenhuma pessoa encontrada</p>
                        </td>
                      </tr>
                    ) : (
                      people.map((person) => (
                        <tr key={person.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <User className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {person.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {person.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {person.type === 'student' ? 'Aluno' : person.type === 'parent' ? 'Responsável' : 'Cliente'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">{person.document || '-'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm font-medium ${
                              (person.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(person.balance || 0)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right space-x-2">
                            <button
                              onClick={() => {
                                setNewCredit(prev => ({ ...prev, person_id: person.id.toString() }));
                                setShowCreditModal(true);
                              }}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              Adicionar Crédito
                            </button>
                            <button 
                              onClick={() => navigate('/dashboard/student-extract')}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Ver Extrato
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
        {/* New Receivable Modal */}
        {showNewReceivableModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Nova Conta a Receber</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pessoa
                </label>
                <select
                  value={newReceivable.person_id}
                  onChange={(e) => setNewReceivable(prev => ({ ...prev, person_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Selecione uma pessoa</option>
                  {people.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name} - {person.type === 'student' ? 'Aluno' : person.type === 'parent' ? 'Responsável' : 'Cliente'}
                      {person.document && ` (${person.document})`}
                    </option>
                  ))}
                </select>
                {people.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Nenhuma pessoa cadastrada. <a href="/dashboard/people/new" className="text-blue-600 hover:underline">Cadastre aqui</a>
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={newReceivable.type}
                  onChange={(e) => setNewReceivable(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="fee">Taxa</option>
                  <option value="product">Produto</option>
                  <option value="service">Serviço</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <Input
                  label="Descrição"
                  value={newReceivable.description}
                  onChange={(e) => setNewReceivable(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição da cobrança"
                  required
                />
              </div>
              
              <Input
                label="Valor"
                type="number"
                step="0.01"
                value={newReceivable.amount}
                onChange={(e) => setNewReceivable(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0,00"
                required
              />
              
              <Input
                label="Data de Vencimento"
                type="date"
                value={newReceivable.due_date}
                onChange={(e) => setNewReceivable(prev => ({ ...prev, due_date: e.target.value }))}
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridade
                </label>
                <select
                  value={newReceivable.priority}
                  onChange={(e) => setNewReceivable(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                </select>
              </div>
              
              <div>
                {/* Empty div for grid alignment */}
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={newReceivable.notes}
                  onChange={(e) => setNewReceivable(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowNewReceivableModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateReceivable}
                className="flex-1"
              >
                Criar Cobrança
              </Button>
            </div>          </div>
        </div>
      )}      {/* Credit Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Adicionar Crédito</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pessoa
                </label>
                <select
                  value={newCredit.person_id}
                  onChange={(e) => setNewCredit(prev => ({ ...prev, person_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Selecione uma pessoa</option>
                  {people.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name} - {person.type === 'student' ? 'Aluno' : person.type === 'parent' ? 'Responsável' : 'Cliente'}
                      {person.document && ` (${person.document})`}
                    </option>
                  ))}
                </select>
                {people.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Nenhuma pessoa cadastrada. <a href="/dashboard/people/new" className="text-blue-600 hover:underline">Cadastre aqui</a>
                  </p>
                )}
              </div>
              
              <Input
                label="Valor do Crédito"
                type="number"
                step="0.01"
                value={newCredit.amount}
                onChange={(e) => setNewCredit(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0,00"
                required
              />
              
              <Input
                label="Descrição"
                value={newCredit.description}
                onChange={(e) => setNewCredit(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrição do crédito"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={newCredit.notes}
                  onChange={(e) => setNewCredit(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Motivo do crédito, observações..."
                />
              </div>
            </div>
              <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowCreditModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>              <Button
                onClick={handleAddCredit}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Adicionar Crédito
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedReceivable && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Detalhes da Conta</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Aluno</label>
                  <p className="text-lg font-medium text-gray-900">{selectedReceivable.student_name}</p>
                  <p className="text-sm text-gray-500">{selectedReceivable.student_class}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Responsável</label>
                  <p className="text-lg font-medium text-gray-900">{selectedReceivable.parent_name}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500">Descrição</label>
                <p className="text-gray-900">{selectedReceivable.description}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Valor</label>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(selectedReceivable.amount)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Vencimento</label>
                  <p className="text-gray-900">{formatDate(selectedReceivable.due_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedReceivable.status)}`}>
                    {getStatusIcon(selectedReceivable.status)}
                    <span className="ml-1">{getStatusLabel(selectedReceivable.status)}</span>
                  </span>
                </div>
              </div>
            </div>
              <div className="mt-6 flex space-x-3">
              <Button variant="outline" className="flex-1">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
              {selectedReceivable.status === 'pending' && (
                <Button className="flex-1">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marcar como Pago
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Fim do Details Modal */}
    </div>
  );
};

export default AccountsReceivablePage;
